import {Divider} from 'antd'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import Pure from 'components/Pure'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useContext} from 'react'
import {withRouter} from 'react-router-dom'
import {compose, nest, withProps} from 'recompose'
import {createRequiredLogin, ModalLayout, ModalLogin,} from 'routes/createRequiredLogin'
import {staticPaths} from 'routes/staticPaths'
import {BoxPlaceholder, NullAsDefault, renderSelf} from 'views/Shared'
import MainHeader, {LogoHeader} from '../MainPage/MainHeader'
import CourseContext, {CourseProvider} from './Course/CourseContext'
import CourseSettingsLazy from './Course/Settings'
import CourseSettingsContent from './Course/Settings/CourseContent'
import CourseSettingHeader from './Course/Settings/CourseSettingHeader'
import {courseSettings} from './Course/Settings/CourseSettings'
import {CustomKeys} from './CustomKeys'
import {AppEntities} from './enums'
import Event from './Event'
import EventBanner from './Event/components/EventBanner'
import EventContent from './Event/components/EventContent'
import EventNav from './Event/components/EventNav'
import EventSider from './Event/components/EventSider'
import EventContext, {EventProvider} from './Event/EventContext'
import EventLayout from './Event/EventLayout'
import {eventRouteEntities} from './Event/eventRouteEntities'
import EventSettingsLazy from './Event/Settings'
import EventSettingsContent from './Event/Settings/EventSettingContent'
import EventSettingHeader from './Event/Settings/EventSettingHeader'
import {eventSettings} from './Event/Settings/EventSettings'
import ExaminationContext, {ExaminationProvider,} from './Exam/ExaminationContext'
import ExaminationSettingsLazy from './Examination/Settings'
import ExaminationSettingsContent from './Examination/Settings/ExaminationSettingContent'
import ExaminationSettingHeader from './Examination/Settings/ExaminationSettingHeader'
import {examinationSettings} from './Examination/Settings/ExaminationSettings'
import {ModuleLayoutWrapper} from './factory/createModule'
import createModuleLayout from './factory/createModuleLayout'
import createSettingLayout from './factory/createSettingLayout'
import {mapPathParam} from './functions/routerHelper'
import Organization from './Organization'
import OrganizationBanner from './Organization/components/OrganizationBanner'
import OrganizationContent from './Organization/components/OrganizationContent'
import OrganizationNav from './Organization/components/OrganizationNav'
import OrganizationSider from './Organization/components/OrganizationSider'
import OrganizationContainerContext, {OrganizationContainerProvider,} from './Organization/OrganizationContainerContext'
import OrganizationLayout from './Organization/OrganizationLayout'
import {organizationRouteEntities} from './Organization/organizationRouteEntities'
import OrganizationSettingLazy from './Organization/Settings'
import OrganizationSettingsContent from './Organization/Settings/OrganizationSettingContent'
import OrganizationSettingHeader from './Organization/Settings/OrganizationSettingHeader'
import {organizationSettings} from './Organization/Settings/OrganizationSettings'
import DefaultTemplate from './Templates/DefaultTemplate'
import PageTemplate from './Templates/PageTemplate'
import User from './User'
import UserBanner from './User/components/UserBanner'
import UserContent from './User/components/UserContent'
import UserNav from './User/components/UserNav'
import UserSider from './User/components/UserSider'
import UserSettingsContent from './User/Settings/UserSettingContent'
import UserSettingHeader from './User/Settings/UserSettingHeader'
import {userSettings} from './User/Settings/UserSettings'
import UserContext, {UserProvider} from './User/UserContext'
import UserLayout from './User/UserLayout'
import {userRouteEntities} from './User/userRouteEntities'
import CoursePage from "./Course/CourseSet/CoursePage.lazy";
import {createLazyComponent} from "../../helpers/createLazyComponent";
import DepartmentPage from "./Deparment/containers/DepartmentPage.lazy";
import ShopPage from "./Shop/containers/ShopPage.lazy";
import ProductPage from "./Product/containers/ProductPage.lazy";

export const gPaths = Object.freeze({
    missal: '/missal/:id',
    missalPage: '/missal/:id/page/:page_nbr',
    missalVersion: '/missal-version/:id',
    music: '/song/:id',
    definition: '/definition/:id',
    dictionary: '/dictionary/:id',
    book: '/book/:id',
    profile: '/profile/:id',
    wog: '/wog/:id',
    catholicHouseholdBook: '/catholic-household-book/:id',
    user: '/user/:id',
    topic: '/t/:id',
    event: '/event/:id',
    category: '/category/:id',
    organization_domain: '',
    organization: '/organization/:id',
    department: '/group/:id',
    shop: '/shop/:id',
    product: '/product/:id',
    userSettings: '/user/:id/settings',
    organizationSettings_domain: '/settings',
    examination: '/examination/:id',
    course: '/course/:id',
    course_class: '/classroom/:id',
    course_class_exam: '/course-class-exam/:id',
    eventSettings: '/event/:id/settings',
    courseSettings: '/course/:id/settings',
    examinationSettings: '/examination/:id/settings',
    organizationSettings: '/organization/:id/settings',
    churchDetail: '/church/:id',
})


export const {modalWidth, headerHeight} = {
    modalWidth: {
        small: 560,
        medium: 640,
        large: 720,
    },
    headerHeight: 'var(--header-height)',
}

export const withModalLayout = (Component, props) =>
    nest(
        Pure,
        withProps({
            width: modalWidth.medium,
            ...props,
        })(ModalLayout),
        withProps(props)(Component)
    )

const settingRoutes = [
    {
        path: gPaths.eventSettings,
        Provider: EventProvider,
        component: EventSettingsLazy,
    },
    {
        path: gPaths.organizationSettings,
        Provider: OrganizationContainerProvider,
        component: OrganizationSettingLazy,
    },
    {
        path: gPaths.examinationSettings,
        Provider: ExaminationProvider,
        component: ExaminationSettingsLazy,
    },
    {
        path: gPaths.courseSettings,
        Provider: CourseProvider,
        component: CourseSettingsLazy,
    },
].map(({component: Component, Provider, ...route}) => ({
    ...route,
    component: withRouter(({match}) => (
        <Provider item={match.params.id}>
            <Component/>
        </Provider>
    )),
}))

const settingModalRoutes = [
    {
        ...CustomKeys.add_host,
        component: createLazyComponent(() => import('./Host/components/AddHostModal')),
    },
    {
        ...CustomKeys.edit_owner,
        component: createLazyComponent(() => import('./custom/OwnerPicker')),
    },
].map(({name, path, component}) => ({
    name,
    path,
    component,
}))

export const gSettingModalRoutes = (name, state = {}) => {
    const routes = settingModalRoutes.reduce((prev, {name, path}) => {
        return {
            ...prev,
            [name]: {
                pathname: path,
            },
        }
    }, {})
    return {
        ..._.get(routes, name, {}),
        state: {
            isModal: true,
            ...state,
        },
    }
}

export const gConfigs = {
    fixedHeader: false,
    styles: {
        headerHeight,
        tabBar: {
            zIndex: 11,
            top: `calc(var(--header-visible-height) - 1px)`,
            position: 'sticky',
            background: 'var(--background-50)',
            padding: '0 0.5rem'
        },
    },
    styles2: {
        headerHeight,
        tabBar: {
            zIndex: 11,
            top: `var(--header-visible-height)`,
            position: 'sticky',
            background: 'var(--background-200)',
            borderRadius: '0.5rem 0.5rem 0 0',
            // gridGap: '0.75rem',
            // gap: '0.75rem'
        },
    },
    dateFormat: 'DD/MM/YYYY',
    timeFormat: 'HH:mm',
    impCatePath: 'impact_categories',
    settingRoute: {
        name: 'settings',
        path: '/settings',
        withPath:
            ({prop, id}) =>
                (route = '/:prop/:id/settings') =>
                    mapPathParam(route, {
                        prop,
                        id,
                    }),
    },
    gRoutes: [
        ...settingRoutes,
        {
            path: gPaths.user,
            component: User,
        },
        {
            path: gPaths.event,
            component: Event,
        },
        {
            path: gPaths.organization,
            component: Organization,
        },
        {
            path: gPaths.department,
            component: DepartmentPage,
        },
        {
            path: gPaths.shop,
            component: ShopPage,
        },
        {
            path: gPaths.product,
            component: ProductPage,
        },
        {
            path: gPaths.course,
            component: CoursePage,
        },
    ],
    gModalRoutes: [
        {
            name: 'select-primary-organization',
            component: createLazyComponent(() => import("../Workspace/components/AdminOrganizations")),
            path: staticPaths.selectPrimaryOrganization.pathname,
            params: {
                width: modalWidth.small,
            },
        },
        {
            name: 'create-organization',
            component: createLazyComponent(() => import('./Organization/CreateOrganization')),
            path: staticPaths.createOrganization.pathname,
        },
        ...settingModalRoutes,
    ].map(({component, title, params, ...route}) => ({
        ...route,
        exact: true,
        withSubDomain: true,
        component: createRequiredLogin(ModalLogin)(
            withModalLayout(component, {
                ...params,
            })
        ),
    })),
}

const DefaultModuleLayout = compose(withTranslate)(
    ({translate, location, history, match, ...props}) =>
        createModuleLayout({
            Sider: withProps({
                title: translate('Sider'),
            })(BoxPlaceholder),
            Banner: withProps({
                title: translate('Banner'),
            })(BoxPlaceholder),
            Template: PageTemplate,
            ...props,
        })(<BoxPlaceholder title={translate('Content')}/>)
)

const LayoutRenderer = ({
                            extra = null,
                            Content = NullAsDefault,
                            Layout = DefaultModuleLayout,
                            location,
                            history,
                            match,
                            ...props
                        }) => (
    <Pure>
        <Layout {...props}>
            <Content/>
            {extra}
        </Layout>
    </Pure>
)

const CustomLayoutRenderer = ({
                                  extra = null,
                                  Content = NullAsDefault,
                                  Layout = DefaultModuleLayout,
                                  location,
                                  history,
                                  match,
                                  ...props
                              }) => (
    <Pure>
        <Layout
            {...props}
            {...{Content}}>
            {extra}
        </Layout>
    </Pure>
)

const SettingLayoutRenderer = ({
                                   Content = NullAsDefault,
                                   Header = NullAsDefault,
                                   Layout = DefaultModuleLayout,
                                   withBreadcrumbs = renderSelf,
                                   location,
                                   history,
                                   match,
                                   ...props
                               }) => {
    const translate = useTranslate()
    const {value} = useContext(ModuleContext)
    const current = _.get(value, 'Context')
    const context = useContext(current) || {}
    return (
        <div className="h-full">
            <ModuleLayoutWrapper
                Footer={withProps({
                    Title: (props) => (
                        <LayoutContext.Consumer>
                            {({isSm}) => isSm && <LogoHeader {...props} />}
                        </LayoutContext.Consumer>
                    ),
                    renderFooter: NullAsDefault,
                })(MainHeader)}
                visibleLeftDrawer={true}
            />
            <div className="h-full space-y-3">
                <div className="h-full p-2 lg:py-2">
                    <Header {...context} />
                    <Divider type="horizontal"/>
                    <Pure>
                        <Layout
                            {...props}
                            {...{Content}}
                        />
                    </Pure>
                </div>
            </div>
        </div>
    )
}

const renderBreadcrumbs = (context, translate = renderSelf) => [
    {
        path: '',
        breadcrumbName: translate('settings'),
    },
]

const SettingLayout = ({
                           Template,
                           menuItems,
                           Content = NullAsDefault,
                           routeEntities,
                           ...props
                       }) =>
    createSettingLayout({
        menuItems,
        Template,
        routes: routeEntities,
        ...props,
    })(Content)

const gModules = {
    [AppEntities.USER]: {
        Nav: UserNav,
        Sider: UserSider,
        Banner: UserBanner,
        Layout: UserLayout,
        Template: PageTemplate,
        Content: UserContent,
        Context: UserContext,
        Provider: UserProvider,
        LayoutRenderer,
        routeEntities: userRouteEntities,
        entity: AppEntities.USER,
        settings: userSettings,
    },
    [AppEntities.EVENT]: {
        Nav: EventNav,
        Sider: EventSider,
        Banner: EventBanner,
        Layout: EventLayout,
        Template: PageTemplate,
        Content: EventContent,
        Context: EventContext,
        Provider: EventProvider,
        LayoutRenderer,
        routeEntities: eventRouteEntities,
        entity: AppEntities.EVENT,
        settings: eventSettings,
    },
    [AppEntities.ORGANIZATION]: {
        Nav: OrganizationNav,
        Sider: OrganizationSider,
        Banner: OrganizationBanner,
        Layout: OrganizationLayout,
        Template: PageTemplate,
        Content: OrganizationContent,
        Context: OrganizationContainerContext,
        Provider: OrganizationContainerProvider,
        LayoutRenderer,
        routeEntities: organizationRouteEntities,
        entity: AppEntities.ORGANIZATION,
        settings: organizationSettings,
    },
}

const settingModules = {
    [AppEntities.EVENT]: {
        bindingName: 'event-settings',
        Layout: SettingLayout,
        Template: DefaultTemplate,
        Content: EventSettingsContent,
        Context: EventContext,
        Provider: EventProvider,
        LayoutRenderer: SettingLayoutRenderer,
        Header: EventSettingHeader,
        withBreadcrumbs: renderBreadcrumbs,
        menuItems: eventSettings.menuItems,
        routeEntities: eventSettings.routeEntities,
        entity: AppEntities.EVENT,
    },
    [AppEntities.ORGANIZATION]: {
        bindingName: 'organization-settings',
        Layout: SettingLayout,
        Template: DefaultTemplate,
        Content: OrganizationSettingsContent,
        Context: OrganizationContainerContext,
        Provider: OrganizationContainerProvider,
        LayoutRenderer: SettingLayoutRenderer,
        Header: OrganizationSettingHeader,
        withBreadcrumbs: renderBreadcrumbs,
        menuItems: organizationSettings.menuItems,
        routeEntities: organizationSettings.routeEntities,
        entity: AppEntities.ORGANIZATION,
    },
    [AppEntities.EXAMINATION]: {
        bindingName: 'examination-settings',
        Layout: SettingLayout,
        Template: DefaultTemplate,
        Content: ExaminationSettingsContent,
        Context: ExaminationContext,
        Provider: ExaminationProvider,
        LayoutRenderer: SettingLayoutRenderer,
        Header: ExaminationSettingHeader,
        withBreadcrumbs: renderBreadcrumbs,
        menuItems: examinationSettings.menuItems,
        routeEntities: examinationSettings.routeEntities,
        entity: AppEntities.EXAMINATION,
    },
    [AppEntities.COURSE]: {
        bindingName: 'course-settings',
        Layout: SettingLayout,
        Template: DefaultTemplate,
        Content: CourseSettingsContent,
        Context: CourseContext,
        Provider: CourseProvider,
        LayoutRenderer: SettingLayoutRenderer,
        Header: CourseSettingHeader,
        withBreadcrumbs: renderBreadcrumbs,
        menuItems: courseSettings.menuItems,
        routeEntities: courseSettings.routeEntities,
        entity: AppEntities.COURSE,
    },
    [AppEntities.USER]: {
        bindingName: 'user-settings',
        Layout: SettingLayout,
        Template: DefaultTemplate,
        Content: UserSettingsContent,
        Context: UserContext,
        Provider: UserProvider,
        LayoutRenderer: SettingLayoutRenderer,
        Header: UserSettingHeader,
        withBreadcrumbs: renderBreadcrumbs,
        menuItems: userSettings.menuItems,
        routeEntities: userSettings.routeEntities,
        entity: AppEntities.USER,
    },
}

export const ModuleContainer = ({
                                    name,
                                    children,
                                    useSettings = false,
                                    configs = gConfigs,
                                    modules = gModules,
                                }) => {
    return children({
        name,
        configs,
        modules,
        useSettings,
        settingModules,
    })
}

export const ModuleContext = React.createContext({})

export const ModuleProvider = ({value, children}) => (
    <ModuleContext.Provider
        key={value}
        value={{value}}>
        {children}
    </ModuleContext.Provider>
)
